import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { hasScope } from '@nyce/admin-kit/api';
import { FormDialog, useConfirm } from '@nyce/admin-kit/console';
import { Button, Chip, Switch, TextField } from '@nyce/admin-kit/ui';
import {
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  useAsync,
  useNotice,
} from '../../components/common';
import { SCOPES } from '../../api';
import { useHubLogin } from '../../session';
import { identityPath, targetSession, useTargets } from '../../identity/session';
import { TenantPicker, allTenants } from '../../identity/TenantPicker';
import { fmtDate } from '../../identity/format';
import type { Tenant } from '../../identity/api';
import { useDesignWrite } from '../api/client';
import {
  CODE_RX,
  codeFrom,
  designTenants,
  normalizeCode,
  type DesignError,
  type DesignTenant,
} from '../api/tenants';
import '../design.css';

/** The AUTH tenants of the active environment by id, when the operator may read them there (else null: ids are shown). */
export function useAuthTenants(target: string) {
  const authRead = hasScope(useHubLogin()?.scopes, SCOPES.authRead);
  return useAsync(
    async () =>
      authRead && targetSession(target).isSignedIn()
        ? new Map((await allTenants(target)).map((t) => [t.id, t]))
        : null,
    [target, authRead]
  );
}

/** Design's `{code, field}` on a failed call, if it sent one. */
export const designError = (e: unknown): DesignError | null => {
  const body = (e as { body?: Partial<DesignError> } | null)?.body;
  return body?.code
    ? { error: body.error ?? (e as Error).message, code: body.code, field: body.field }
    : null;
};

/** `users of this tenant lose access` when switching off; switching on needs no confirmation. */
export function useEnableSwitch(target: string, onDone: (t: DesignTenant) => void) {
  const confirm = useConfirm();
  const toast = useNotice();
  const toggle = async (t: DesignTenant, enabled: boolean) => {
    if (
      !enabled &&
      !(await confirm({
        title: `Disable ${t.name} in NYCE Design?`,
        message:
          'Users of this tenant lose access within 30 s. Their layouts stay; enable it again to give access back.',
        confirmLabel: 'Disable',
        danger: true,
      }))
    )
      return;
    try {
      const next = await designTenants(target).update(t.id, {
        code: t.code,
        name: t.name,
        enabled,
        sso: t.sso,
      });
      toast.success(`${next.name} ${enabled ? 'enabled' : 'disabled'}`);
      onDone(next);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  return { toggle, toast };
}

/** NYCE Design's tenant registry in one environment: which AUTH tenants may use Design (ADR 0046 in Design). */
export function DesignTenantsPage() {
  const { active: target } = useTargets();
  const canWrite = useDesignWrite();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const data = useAsync(() => designTenants(target).list(), [target]);
  const authTenants = useAuthTenants(target);
  const { toggle, toast } = useEnableSwitch(target, () => void data.reload());
  const [adding, setAdding] = useState(false);

  const authName = (id: string) => authTenants.data?.get(id)?.name ?? null;
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data.data ?? [])
      .filter(
        (t) =>
          !needle ||
          [t.code, t.name, authTenants.data?.get(t.id)?.name ?? '', t.id].some((s) =>
            s.toLowerCase().includes(needle)
          )
      )
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [data.data, authTenants.data, q]);

  const setFilter = (v: string) =>
    setParams(
      (cur) => {
        const next = new URLSearchParams(cur);
        if (v) next.set('q', v);
        else next.delete('q');
        return next;
      },
      { replace: true }
    );

  return (
    <Page
      title="NYCE Design tenants"
      description="The AUTH tenants that may use NYCE Design in this environment. Users sign in with the tenant's code; a disabled tenant loses access within 30 s. Disabling is the off switch: a tenant is never deleted."
      actions={
        canWrite && (
          <Button size="sm" onClick={() => setAdding(true)}>
            Add tenant
          </Button>
        )
      }
    >
      {toast.element}
      <ErrorBox error={data.error} />
      <div className="nyk-card">
        <div className="ds-toolbar">
          <TextField
            label="Filter"
            value={q}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="code or name"
          />
        </div>
        <Loading loading={data.loading && !data.data} />
        {!!data.data?.length && (
          <TableScroller>
            <table
              className="nyk-ui-table nyk-cards"
              data-testid="design-tenants"
              aria-label="NYCE Design tenants"
            >
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>AUTH tenant</th>
                  <th>Enabled</th>
                  <th>SSO</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} data-tenant={t.id}>
                    <td className="nyk-card-title nyk-mono">
                      <Link to={identityPath(`/design/tenants/${encodeURIComponent(t.id)}`, target)}>
                        {t.code}
                      </Link>
                    </td>
                    <td data-label="Name">{t.name}</td>
                    <td data-label="AUTH tenant">
                      {authName(t.id) ?? <span className="nyk-mono">{t.id}</span>}
                    </td>
                    <td data-label="Enabled">
                      {canWrite ? (
                        <Switch
                          checked={t.enabled}
                          label={t.enabled ? 'on' : 'off'}
                          onChange={(on) => void toggle(t, on)}
                        />
                      ) : (
                        <Chip size="sm" className={t.enabled ? 'nyk-tone-green' : 'nyk-tone-grey'}>
                          {t.enabled ? 'enabled' : 'disabled'}
                        </Chip>
                      )}
                    </td>
                    <td data-label="SSO">
                      {t.sso ? (
                        <Chip size="sm" className="nyk-tone-blue">
                          SSO
                        </Chip>
                      ) : (
                        <span className="nyk-ui-hint">—</span>
                      )}
                    </td>
                    <td data-label="Updated">
                      {fmtDate(t.updatedAt)}
                      {t.updatedBy && <span className="nyk-ui-hint"> · {t.updatedBy}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        )}
        {data.data && !data.data.length && (
          <EmptyState
            title="No tenants yet."
            hint="Add the AUTH tenants of customers that use NYCE Design."
          />
        )}
        {!!data.data?.length && !rows.length && <EmptyState title="No tenant matches the filter." />}
      </div>
      {adding && (
        <AddTenantDialog
          target={target}
          registered={(data.data ?? []).map((t) => t.id)}
          onClose={() => setAdding(false)}
          onCreated={(t) => {
            setAdding(false);
            toast.success(`Added ${t.name}`);
            void data.reload();
          }}
        />
      )}
    </Page>
  );
}

/** Code field: lowercased while typing, the rule shown until it holds; a server error on `code` wins. */
export function CodeField({
  value,
  onChange,
  error,
  hint,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string;
  hint?: string;
}) {
  const bad = value.trim() !== '' && !CODE_RX.test(normalizeCode(value));
  return (
    <TextField
      label="Code"
      className="nyk-mono"
      value={value}
      onChange={(e) => onChange(e.target.value.toLowerCase())}
      error={error ?? (bad ? '2–32 of a–z, 0–9 and -, not starting or ending with -' : undefined)}
      hint={hint ?? 'What users type at sign-in, e.g. stadium.'}
    />
  );
}

function AddTenantDialog({
  target,
  registered,
  onClose,
  onCreated,
}: {
  target: string;
  registered: string[];
  onClose: () => void;
  onCreated: (t: DesignTenant) => void;
}) {
  const [id, setId] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  // Off by default: a tenant that takes over existing data (Design's first-deploy re-key) must exist disabled until
  // Design has restarted and moved it; enabled at once, its first user would seed it empty.
  const [enabled, setEnabled] = useState(false);
  const [sso, setSso] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const picked = (t: Tenant | null) => {
    if (!t) return;
    setCode((c) => c || codeFrom(t.name));
    setName((n) => n || t.name);
  };
  const valid = /^[0-9a-f-]{36}$/i.test(id) && CODE_RX.test(normalizeCode(code)) && name.trim().length > 0;

  const submit = async () => {
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      onCreated(
        await designTenants(target).create({ id, code: normalizeCode(code), name: name.trim(), enabled, sso })
      );
    } catch (e) {
      const d = designError(e);
      if (d?.field) setFieldErrors({ [d.field]: d.error });
      else setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormDialog
      open
      title="Add a NYCE Design tenant"
      size="sm"
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel="Create"
      canSubmit={valid}
      busy={busy}
      error={error}
      draft={{ id, code, name, enabled, sso }}
      formClassName="ds-form"
    >
      <TenantPicker
        target={target}
        value={id}
        onChange={setId}
        onTenant={picked}
        exclude={registered}
        allowNone={false}
        error={fieldErrors.id}
        hint="The customer's tenant in this environment's AUTH; its id becomes the Design tenant id."
      />
      <CodeField
        value={code}
        onChange={(v) => {
          setCode(v);
          setFieldErrors(({ code: _, ...rest }) => rest);
        }}
        error={fieldErrors.code}
      />
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={fieldErrors.name}
      />
      <Switch checked={enabled} onChange={setEnabled} label="Enabled" aria-describedby="ds-enabled-hint" />
      <p className="nyk-ui-hint" id="ds-enabled-hint">
        Leave it off until the tenant is ready: on a first deploy create it disabled, restart Design so the
        existing layouts move to it, then enable it here.
      </p>
      <Switch checked={sso} onChange={setSso} label="Single sign-on (Microsoft)" />
    </FormDialog>
  );
}
